import { useState } from 'react';
import {
  useSeoKeywords,
  useCreateSeoKeyword,
  useUpdatePosition,
  useDeleteSeoKeyword,
  useImportKeywordsFromGsc,
  useBulkUpdatePositions,
  useFetchVolumes,
  useTrackInMonitor,
} from '../hooks/useSeoKeywords';
import Modal from '../components/ui/Modal';
import StatCard from '../components/ui/StatCard';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';

const CATEGORIES = [
  { value: '', label: 'Alle' },
  { value: 'primary', label: 'Primär' },
  { value: 'secondary', label: 'Sekundär' },
  { value: 'brand', label: 'Brand' },
];

const CAT_COLORS = {
  primary: 'bg-teal/10 text-teal',
  secondary: 'bg-amber-50 text-amber-600',
  brand: 'bg-purple-50 text-purple-600',
};

const CAT_LABELS = {
  primary: 'Primär',
  secondary: 'Sekundär',
  brand: 'Brand',
};

function PositionBadge({ current, start }) {
  if (current === null || current === undefined) {
    return <span className="text-gray-400 text-[12px]">—</span>;
  }
  return (
    <span className="font-semibold text-[14px]">{Number(current).toFixed(1)}</span>
  );
}

function TrendIndicator({ change }) {
  if (change === null || change === undefined) return null;
  const improved = change > 0;
  const declined = change < 0;
  const abs = Math.abs(change).toFixed(1);

  if (change === 0) {
    return <span className="text-gray-400 text-[11px]">±0</span>;
  }

  return (
    <span className={`text-[11px] font-medium ${improved ? 'text-green-600' : 'text-red-500'}`}>
      {improved ? '↑' : '↓'} {abs}
    </span>
  );
}

function TargetBadge({ onTarget, target }) {
  if (onTarget === null || onTarget === undefined || !target) {
    return <span className="text-gray-400 text-[12px]">{target || '—'}</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] font-medium px-2 py-0.5 rounded-full ${
      onTarget ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
    }`}>
      {onTarget && <span>✓</span>}
      {target}
    </span>
  );
}

export default function SeoRankingChecker() {
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editPositionId, setEditPositionId] = useState(null);
  const [newPosition, setNewPosition] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkPositions, setBulkPositions] = useState({});

  const { data, isLoading } = useSeoKeywords({ category: filter || undefined });
  const createMutation = useCreateSeoKeyword();
  const updatePositionMutation = useUpdatePosition();
  const deleteMutation = useDeleteSeoKeyword();
  const importGscMutation = useImportKeywordsFromGsc();
  const bulkUpdateMutation = useBulkUpdatePositions();
  const fetchVolumesMutation = useFetchVolumes();
  const trackMonitorMutation = useTrackInMonitor();
  const [volumeToast, setVolumeToast] = useState(null);

  const keywords = data?.keywords || [];
  const stats = data?.stats || {};

  const filtered = search
    ? keywords.filter(
        (kw) =>
          kw.keyword.toLowerCase().includes(search.toLowerCase()) ||
          (kw.target_url && kw.target_url.toLowerCase().includes(search.toLowerCase()))
      )
    : keywords;

  const handleSavePosition = (id) => {
    if (!newPosition) return;
    updatePositionMutation.mutate(
      { id, position: parseFloat(newPosition) },
      { onSuccess: () => { setEditPositionId(null); setNewPosition(''); } }
    );
  };

  const handleBulkSave = () => {
    const updates = Object.entries(bulkPositions)
      .filter(([, pos]) => pos !== '')
      .map(([id, position]) => ({ id: parseInt(id), position: parseFloat(position) }));

    if (updates.length === 0) return;
    bulkUpdateMutation.mutate(updates, {
      onSuccess: () => { setBulkMode(false); setBulkPositions({}); },
    });
  };

  const handleAdd = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    createMutation.mutate(
      {
        keyword: fd.get('keyword'),
        target_url: fd.get('target_url') || null,
        category: fd.get('category'),
        position_start: fd.get('position_start') ? parseFloat(fd.get('position_start')) : null,
        position_current: fd.get('position_start') ? parseFloat(fd.get('position_start')) : null,
        target_position: fd.get('target_position') || null,
        search_volume: fd.get('search_volume') ? parseInt(fd.get('search_volume')) : null,
      },
      { onSuccess: () => setShowAdd(false) }
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold text-gray-900">SEO Ranking Checker</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Keyword-Rankings für das aktive Projekt tracken</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setVolumeToast(null);
              importGscMutation.mutate(
                { days: 28, limit: 100 },
                {
                  onSuccess: (data) =>
                    setVolumeToast({
                      type: 'ok',
                      text: `GSC-Import: ${data.imported} neu, ${data.updated} aktualisiert (letzte ${data.days} Tage)`,
                    }),
                  onError: (err) =>
                    setVolumeToast({
                      type: 'err',
                      text: err.response?.data?.message || err.message,
                    }),
                }
              );
            }}
            disabled={importGscMutation.isPending}
            className="px-3.5 py-2 rounded-lg text-[13px] font-medium bg-teal text-white hover:opacity-90 disabled:opacity-50"
            title="Top-Queries aus der Google Search Console importieren"
          >
            {importGscMutation.isPending ? 'Importiere…' : '⬇ Aus Search Console importieren'}
          </button>
          <button
            onClick={() => {
              setVolumeToast(null);
              fetchVolumesMutation.mutate(undefined, {
                onSuccess: (data) =>
                  setVolumeToast({
                    type: 'ok',
                    text: `${data.updated} von ${data.fetched_rows} Keywords aktualisiert${
                      data.unmatched_count ? ` · ${data.unmatched_count} ohne XOVI-Match` : ''
                    }`,
                  }),
                onError: (err) =>
                  setVolumeToast({
                    type: 'err',
                    text: err.response?.data?.message || err.message,
                  }),
              });
            }}
            disabled={fetchVolumesMutation.isPending}
            className="px-3.5 py-2 rounded-lg text-[13px] font-medium border bg-white text-gray-700 border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            title="Suchvolumen via XOVI aktualisieren"
          >
            {fetchVolumesMutation.isPending ? 'XOVI lädt…' : '◉ Volumen aktualisieren'}
          </button>
          <button
            onClick={() => {
              setVolumeToast(null);
              trackMonitorMutation.mutate(
                { only_missing: true },
                {
                  onSuccess: (data) =>
                    setVolumeToast({
                      type: 'ok',
                      text: `${data.added} Keywords dem XOVI-Monitor hinzugefügt${
                        data.failed_count ? ` · ${data.failed_count} Fehler` : ''
                      }. ${data.note || ''}`,
                    }),
                  onError: (err) =>
                    setVolumeToast({
                      type: 'err',
                      text: err.response?.data?.message || err.message,
                    }),
                },
              );
            }}
            disabled={trackMonitorMutation.isPending}
            className="px-3.5 py-2 rounded-lg text-[13px] font-medium border bg-white text-gray-700 border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            title="Fehlende Keywords dem XOVI Keyword-Monitor hinzufügen"
          >
            {trackMonitorMutation.isPending ? 'XOVI lädt…' : '⊕ Im Monitor tracken'}
          </button>
          <button
            onClick={() => setBulkMode(!bulkMode)}
            className={`px-3.5 py-2 rounded-lg text-[13px] font-medium border ${
              bulkMode
                ? 'bg-teal text-white border-teal'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {bulkMode ? '✓ Bulk-Modus aktiv' : '⟳ Bulk-Update'}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="px-3.5 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
          >
            + Keyword
          </button>
        </div>
      </div>

      {volumeToast && (
        <div
          className={`mb-4 px-4 py-2.5 rounded-lg text-[12.5px] flex items-center justify-between ${
            volumeToast.type === 'ok'
              ? 'bg-teal/[.08] text-teal border border-teal/20'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <span>{volumeToast.type === 'ok' ? '✓ ' : '✕ '}{volumeToast.text}</span>
          <button onClick={() => setVolumeToast(null)} className="text-[14px] opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3.5 mb-5">
        <StatCard icon="🔑" value={stats.total || 0} label="Keywords" sub="Gesamt getrackt" />
        <StatCard icon="📍" value={stats.avg_position || '—'} label="Ø Position" sub="Durchschnitt" />
        <StatCard icon="✓" value={stats.on_target || 0} label="Im Ziel" sub="Target erreicht" />
        <StatCard icon="↑" value={stats.improved || 0} label="Verbessert" sub="Seit Start" />
        <StatCard icon="↓" value={stats.declined || 0} label="Verschlechtert" sub="Seit Start" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex bg-white rounded-lg border border-gray-200 overflow-hidden">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setFilter(cat.value)}
              className={`px-3.5 py-1.5 text-[12px] font-medium transition-colors ${
                filter === cat.value
                  ? 'bg-teal text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Keyword suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-[13px] outline-none focus:border-teal w-[240px]"
        />
        {bulkMode && (
          <button
            onClick={handleBulkSave}
            disabled={bulkUpdateMutation.isPending || Object.keys(bulkPositions).length === 0}
            className="ml-auto px-4 py-1.5 bg-teal text-white rounded-lg text-[12px] font-medium hover:opacity-90 disabled:opacity-50"
          >
            {bulkUpdateMutation.isPending ? 'Speichere…' : `Positionen speichern (${Object.values(bulkPositions).filter(v => v !== '').length})`}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-[13px]">Lade Keywords…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-[13px]">
            {keywords.length === 0 ? 'Noch keine Keywords. Importiere sie aus der MD-Datei.' : 'Keine Treffer.'}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-[11px] text-gray-500 font-medium px-4 py-3">Keyword</th>
                <th className="text-left text-[11px] text-gray-500 font-medium px-4 py-3">Ziel-URL</th>
                <th className="text-left text-[11px] text-gray-500 font-medium px-4 py-3">Kategorie</th>
                <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-3">Start</th>
                <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-3">Aktuell</th>
                <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-3">Trend</th>
                <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-3">Ziel</th>
                <th className="text-right text-[11px] text-gray-500 font-medium px-4 py-3">Vol.</th>
                <th className="text-right text-[11px] text-gray-500 font-medium px-4 py-3">CPC</th>
                <th className="text-right text-[11px] text-gray-500 font-medium px-4 py-3">Impr.</th>
                <th className="text-right text-[11px] text-gray-500 font-medium px-4 py-3">Klicks</th>
                <th className="text-right text-[11px] text-gray-500 font-medium px-4 py-3">CTR</th>
                <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-3">History</th>
                <th className="text-right text-[11px] text-gray-500 font-medium px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((kw, i) => (
                <tr
                  key={kw.id}
                  className={`${i < filtered.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}
                >
                  <td className="px-4 py-3">
                    <span className="text-[13px] font-medium text-gray-900">{kw.keyword}</span>
                  </td>
                  <td className="px-4 py-3">
                    {kw.target_url ? (
                      <a
                        href={`https://konversion.digital${kw.target_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-teal hover:underline"
                      >
                        {kw.target_url}
                      </a>
                    ) : (
                      <span className="text-gray-400 text-[12px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${CAT_COLORS[kw.category]}`}>
                      {CAT_LABELS[kw.category]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-[12px] text-gray-500">
                      {kw.position_start !== null ? Number(kw.position_start).toFixed(1) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {bulkMode ? (
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="999"
                        placeholder={kw.position_current !== null ? Number(kw.position_current).toFixed(1) : '—'}
                        value={bulkPositions[kw.id] || ''}
                        onChange={(e) => setBulkPositions((p) => ({ ...p, [kw.id]: e.target.value }))}
                        className="w-[60px] border border-gray-200 rounded px-2 py-1 text-[12px] text-center outline-none focus:border-teal"
                      />
                    ) : editPositionId === kw.id ? (
                      <div className="flex items-center justify-center gap-1">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="999"
                          autoFocus
                          value={newPosition}
                          onChange={(e) => setNewPosition(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSavePosition(kw.id);
                            if (e.key === 'Escape') { setEditPositionId(null); setNewPosition(''); }
                          }}
                          className="w-[55px] border border-teal rounded px-1.5 py-0.5 text-[12px] text-center outline-none"
                        />
                        <button
                          onClick={() => handleSavePosition(kw.id)}
                          className="text-teal text-[11px] font-bold"
                        >
                          ✓
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditPositionId(kw.id); setNewPosition(kw.position_current !== null ? Number(kw.position_current).toFixed(1) : ''); }}
                        className="hover:bg-teal/5 rounded px-1 py-0.5 transition-colors"
                        title="Klicken zum Aktualisieren"
                      >
                        <PositionBadge current={kw.position_current} start={kw.position_start} />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <TrendIndicator change={kw.position_change} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <TargetBadge onTarget={kw.on_target} target={kw.target_position} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {kw.search_volume != null ? (
                      <span className="text-[12px] text-gray-700 font-medium">{Number(kw.search_volume).toLocaleString('de-DE')}</span>
                    ) : (
                      <span className="text-gray-300 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {kw.cpc != null && Number(kw.cpc) > 0 ? (
                      <span className="text-[12px] text-gray-700 font-medium">{Number(kw.cpc).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                    ) : (
                      <span className="text-gray-300 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {kw.impressions != null ? (
                      <span className="text-[12px] text-gray-600">{Number(kw.impressions).toLocaleString('de-DE')}</span>
                    ) : (
                      <span className="text-gray-300 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {kw.clicks != null ? (
                      <span className="text-[12px] text-gray-600">{kw.clicks}</span>
                    ) : (
                      <span className="text-gray-300 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {kw.ctr != null ? (
                      <span className={`text-[12px] font-medium ${Number(kw.ctr) >= 3 ? 'text-green-600' : Number(kw.ctr) >= 1 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {Number(kw.ctr).toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-gray-300 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {kw.history && kw.history.length > 0 ? (
                      <MiniChart history={kw.history} />
                    ) : (
                      <span className="text-gray-300 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { if (confirm('Keyword löschen?')) deleteMutation.mutate(kw.id); }}
                      className="text-gray-400 hover:text-red-500 text-[12px] transition-colors"
                      title="Löschen"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Last check info */}
      {keywords.length > 0 && (
        <div className="mt-3 text-[11px] text-gray-400 flex items-center gap-4">
          <span>
            Letztes Update:{' '}
            {keywords.find((k) => k.last_checked_at)
              ? new Date(keywords.find((k) => k.last_checked_at).last_checked_at).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Noch nie'}
          </span>
          <span>
            Quelle: Google Search Console / Manuell
          </span>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h2 className="text-[16px] font-bold mb-4">Keyword hinzufügen</h2>
          <form onSubmit={handleAdd}>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="col-span-2">
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Keyword *</label>
                <input name="keyword" required className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Ziel-URL</label>
                <input name="target_url" placeholder="/seite/" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Kategorie</label>
                <select name="category" defaultValue="primary" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal">
                  <option value="primary">Primär</option>
                  <option value="secondary">Sekundär</option>
                  <option value="brand">Brand</option>
                </select>
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Startposition</label>
                <input name="position_start" type="number" step="0.1" min="0" placeholder="z.B. 18.5" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Ziel</label>
                <input name="target_position" placeholder="z.B. Top 10" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Suchvolumen /Monat</label>
                <input name="search_volume" type="number" min="0" placeholder="z.B. 1300" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50 rounded-lg">
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Speichere…' : 'Speichern'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function MiniChart({ history }) {
  if (!history || history.length < 2) {
    return <span className="text-gray-400 text-[11px]">{history?.length || 0} Eintr.</span>;
  }

  const positions = history.map((h) => h.position);
  const max = Math.max(...positions);
  const min = Math.min(...positions);
  const range = max - min || 1;
  const w = 60;
  const h = 20;
  const step = w / (positions.length - 1);

  // Inverted: lower position = higher on chart (better)
  const points = positions.map((p, i) => {
    const x = i * step;
    const y = ((p - min) / range) * (h - 4) + 2;
    return `${x},${y}`;
  }).join(' ');

  const lastImproved = positions.length >= 2 && positions[positions.length - 1] < positions[positions.length - 2];

  return (
    <svg width={w} height={h} className="inline-block">
      <polyline
        points={points}
        fill="none"
        stroke={lastImproved ? '#10B981' : '#EF4444'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
